import { useLocation, matchPath } from 'react-router-dom';
import { pipe, Option, Array as A } from 'effect';

/**
 * Company-id-bearing route patterns. Every company route carries `:companyId`
 * in one of four shapes (see `domain/router/routesPathConfig.js`):
 *   /company/standard/:companyId/*      (workspace)
 *   /company/confidential/:companyId/*  (confidential workspace)
 *   /company/dashboard/:companyId/*     (dashboard)
 *   /company/:companyId/*               (document, configurations, bank-feed, …)
 * The prefixed variants are listed first so they win over the generic one, and
 * their literal segments (`standard`/`confidential`/`dashboard`) must never be
 * mistaken for a company id when the generic pattern is tried.
 */
const COMPANY_ROUTE_PREFIXES: readonly string[] = ['standard', 'confidential', 'dashboard'];
const COMPANY_ROUTE_PATTERNS = [
  ...COMPANY_ROUTE_PREFIXES.map((prefix) => `/company/${prefix}/:companyId/*`),
  '/company/:companyId/*',
] as const;

/** A literal route prefix is never a valid company id. */
const isRoutePrefix = (segment: string): boolean => COMPANY_ROUTE_PREFIXES.includes(segment);

export const extractRouteCompanyId = (pathname: string): string | null =>
  pipe(
    COMPANY_ROUTE_PATTERNS,
    A.filterMap((pattern) =>
      pipe(
        Option.fromNullable(matchPath(pattern, pathname)),
        Option.flatMap((match) => Option.fromNullable(match.params.companyId)),
        // Guards the generic `/company/:companyId/*` pattern from capturing a
        // literal prefix (`standard`/`confidential`/`dashboard`) as the id,
        // e.g. `/company/dashboard` with no id after it.
        Option.filter((companyId) => !isRoutePrefix(companyId)),
      ),
    ),
    A.head,
    Option.getOrNull,
  );

/**
 * Current company id taken from the URL via React Router `matchPath`. Unlike
 * `companyIdSelector` (which derives an asynchronously-hydrated store value and
 * lags behind navigation), the URL carries `:companyId` synchronously the moment
 * a company route matches — so this never returns a stale/null id while a
 * company route is active. Returns `null` when no company route is active.
 */
export const useRouteCompanyId = (): string | null => {
  const { pathname } = useLocation();
  return extractRouteCompanyId(pathname);
};
